import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { Calendar, MapPin, Clock, Flag, ChevronRight, Trophy, Globe, Ticket, Filter, Send, Mail, PlusCircle, Download } from "lucide-react";
import { LEGENDTEAM_URL } from "@/lib/constants";
import { useLang } from "@/context/LanguageContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const SUBMIT_EMAIL = "info@legendmagazine.it";

const catColor = {
  Slalom: "bg-legend-yellow text-legend-ink",
  Historic: "bg-legend-red text-white",
  CIR: "bg-blue-600 text-white",
  CIAR: "bg-emerald-600 text-white",
  CIRT: "bg-orange-700 text-white",
  Drift: "bg-purple-600 text-white",
  MTB: "bg-green-600 text-white",
  Enduro: "bg-amber-600 text-white",
  Trial: "bg-sky-700 text-white",
  EnduroGP: "bg-red-700 text-white",
  TrialGP: "bg-indigo-700 text-white",
  "CIGT Sprint": "bg-blue-700 text-white",
  "CIGT Endurance": "bg-cyan-700 text-white",
  "GT4 Italy": "bg-violet-700 text-white",
  "Bike Park": "bg-lime-600 text-white",
  "MTB Area": "bg-emerald-700 text-white",
  "Raduno MTB": "bg-teal-600 text-white",
  "Sentiero E.S.P.": "bg-green-700 text-white",
  Truck: "bg-stone-600 text-white",
  Trattori: "bg-green-700 text-white",
  Storiche: "bg-amber-700 text-white",
  Raduno: "bg-indigo-600 text-white",
  Evento: "bg-pink-600 text-white",
  Fiera: "bg-lime-700 text-white",
  "Time Attack": "bg-sky-600 text-white",
  Pista: "bg-sky-600 text-white",
  WRC: "bg-red-700 text-white",
  ERC: "bg-cyan-600 text-white",
  F1: "bg-red-600 text-white",
  MotoGP: "bg-orange-600 text-white",
  Moto2: "bg-teal-600 text-white",
  WorldSBK: "bg-rose-700 text-white",
  "Formula E": "bg-cyan-500 text-white",
  Apecar: "bg-yellow-600 text-legend-ink",
  Rally: "bg-slate-700 text-white",
};

const DISC_ORDER = ["Rally", "Storiche", "Drift", "MTB", "Enduro", "Truck", "Trattori", "Pista", "Fiere", "F1", "MotoGP", "Moto2", "Superbike", "Formula E", "Apecar"];

const isUpcoming = (d) => new Date(d) >= new Date(new Date().toDateString());

const LEGEND_HQ = { lat: 43.8643, lng: 12.208 };

const DISCIPLINE_OPTIONS = ["Rally", "Storiche", "Drift", "MTB", "Enduro", "Truck", "Trattori", "Pista", "Fiere", "F1", "MotoGP", "Moto2", "Evento"];

function ProposeEventDialog({ lang }) {
  const IT = lang !== "en";
  const empty = { organizer: "", email: "", title: "", discipline: "Rally", date: "", location: "", description: "", link: "" };
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [f, setF] = useState(empty);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await client.post("/event-submissions", f);
      toast.success(IT ? "Proposta inviata! La redazione la valuterà al più presto." : "Proposal sent! Our editorial team will review it soon.");
      setF(empty);
      setOpen(false);
    } catch {
      toast.error(IT ? "Invio non riuscito. Riprova o scrivici via email." : "Sending failed. Please retry or email us.");
    } finally {
      setSending(false);
    }
  };

  const mailto = `mailto:${SUBMIT_EMAIL}?subject=${encodeURIComponent((IT ? "Proposta evento — " : "Event proposal — ") + (f.title || ""))}&body=${encodeURIComponent(
    `${IT ? "Organizzatore" : "Organizer"}: ${f.organizer}\nEmail: ${f.email}\n${IT ? "Evento" : "Event"}: ${f.title}\n${IT ? "Disciplina" : "Discipline"}: ${f.discipline}\n${IT ? "Data" : "Date"}: ${f.date}\n${IT ? "Luogo" : "Location"}: ${f.location}\nLink: ${f.link}\n\n${f.description}`
  )}`;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="propose-event-btn" className="inline-flex items-center gap-2 bg-legend-red hover:bg-legend-dark text-white text-sm font-bold uppercase tracking-wide px-4 py-2.5 rounded-sm transition-colors">
          <PlusCircle size={16} /> {IT ? "Proponi un evento" : "Submit an event"}
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="propose-event-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black uppercase">{IT ? "Proponi un evento" : "Submit an event"}</DialogTitle>
          <DialogDescription>
            {IT
              ? "Organizzatori e appassionati: inviateci gare, raduni e locandine. La proposta arriva alla redazione di Legend Magazine per la verifica e l'inserimento nel calendario."
              : "Organizers and fans: send us races, meetings and posters. Your proposal reaches the Legend Magazine editorial team for review and calendar listing."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>{IT ? "Organizzatore / Nome" : "Organizer / Name"} *</Label><Input data-testid="propose-organizer" value={f.organizer} onChange={(e) => set("organizer", e.target.value)} required /></div>
            <div><Label>Email *</Label><Input type="email" data-testid="propose-email" value={f.email} onChange={(e) => set("email", e.target.value)} required /></div>
          </div>
          <div><Label>{IT ? "Titolo evento" : "Event title"} *</Label><Input data-testid="propose-title" value={f.title} onChange={(e) => set("title", e.target.value)} required /></div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>{IT ? "Disciplina" : "Discipline"}</Label>
              <select data-testid="propose-discipline" value={f.discipline} onChange={(e) => set("discipline", e.target.value)} className="w-full h-10 px-3 rounded-sm border border-slate-300 dark:border-slate-700 bg-white dark:bg-legend-paper text-sm">
                {DISCIPLINE_OPTIONS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div><Label>{IT ? "Data" : "Date"}</Label><Input type="date" data-testid="propose-date" value={f.date} onChange={(e) => set("date", e.target.value)} /></div>
          </div>
          <div><Label>{IT ? "Luogo" : "Location"}</Label><Input data-testid="propose-location" value={f.location} onChange={(e) => set("location", e.target.value)} placeholder={IT ? "Città (Provincia) — circuito/luogo" : "City (Province) — venue"} /></div>
          <div><Label>{IT ? "Sito / Instagram" : "Website / Instagram"}</Label><Input data-testid="propose-link" value={f.link} onChange={(e) => set("link", e.target.value)} placeholder="https://" /></div>
          <div><Label>{IT ? "Dettagli (orari, categorie, biglietti...)" : "Details (schedule, classes, tickets...)"}</Label><Textarea rows={4} data-testid="propose-description" value={f.description} onChange={(e) => set("description", e.target.value)} /></div>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button type="submit" disabled={sending} data-testid="propose-submit" className="inline-flex items-center gap-2 bg-legend-red hover:bg-legend-dark text-white text-sm font-bold uppercase px-5 py-2.5 rounded-sm disabled:opacity-60">
              <Send size={15} /> {IT ? "Invia alla redazione" : "Send to editorial"}
            </button>
            <a href={mailto} data-testid="propose-mailto" className="inline-flex items-center gap-2 border border-slate-300 dark:border-slate-700 text-sm font-semibold px-4 py-2.5 rounded-sm hover:border-legend-red">
              <Mail size={15} /> {IT ? "Allega locandina via email" : "Attach poster by email"}
            </a>
          </div>
          <p className="text-[11px] text-slate-400">{IT ? "Hai una locandina? Inviala in allegato a" : "Have a poster? Email it to"} <a href={`mailto:${SUBMIT_EMAIL}`} className="text-legend-red underline">{SUBMIT_EMAIL}</a></p>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Calendario() {
  const [races, setRaces] = useState([]);
  const [selected, setSelected] = useState(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { lang, t } = useLang();
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const markers = useRef({});
  const userPan = useRef(false);
  const detailRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 1024);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const [region, setRegion] = useState("Tutti");
  const [discipline, setDiscipline] = useState("Rally");
  const [year, setYear] = useState(String(new Date().getFullYear()));

  const fmt = (d) => new Date(d).toLocaleDateString(t("dateLocale"), { day: "2-digit", month: "long", year: "numeric" });
  const dn = (r) => (lang === "en" ? r.name_en || r.name : r.name);
  const dc = (r) => (lang === "en" ? r.category_en || r.category : r.category);

  useEffect(() => {
    client.get("/races").then((r) => {
      setRaces(r.data);
      const initId = params.get("race");
      const init = r.data.find((x) => x.id === initId) || r.data.find((x) => isUpcoming(x.date) && (x.discipline || "Rally") === "Rally") || r.data[0];
      setSelected(init);
    });
  }, []); // eslint-disable-line

  // init map
  useEffect(() => {
    if (!window.L || !mapRef.current || mapObj.current || races.length === 0) return;
    const map = window.L.map(mapRef.current, { scrollWheelZoom: false, zoomSnap: 0.25 }).setView([LEGEND_HQ.lat, LEGEND_HQ.lng], 5.75);
    window.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap", maxZoom: 18,
    }).addTo(map);
    races.forEach((r) => {
      if (r.lat == null || r.lng == null) return;
      const up = isUpcoming(r.date);
      const icon = window.L.divIcon({
        className: "",
        html: `<div style="background:${up ? "#E4022B" : "#64748b"};width:16px;height:16px;border-radius:50%;border:3px solid #fff;box-shadow:0 0 0 2px ${up ? "#E4022B" : "#64748b"}"></div>`,
        iconSize: [16, 16], iconAnchor: [8, 8],
      });
      const m = window.L.marker([r.lat, r.lng], { icon }).addTo(map)
        .bindPopup(`<strong>${r.name}</strong><br/>${r.location}<br/>${r.date_label || fmt(r.date)}`);
      m.on("click", () => { userPan.current = true; setSelected(r); });
      markers.current[r.id] = m;
    });
    // Legend Team HQ marker
    const hqIcon = window.L.divIcon({
      className: "",
      html: `<div style="background:#F5C518;width:20px;height:20px;border-radius:4px;border:3px solid #0B0B0C;box-shadow:0 0 0 2px #F5C518;display:grid;place-items:center;font-size:11px;font-weight:900;color:#0B0B0C">L</div>`,
      iconSize: [20, 20], iconAnchor: [10, 10],
    });
    window.L.marker([LEGEND_HQ.lat, LEGEND_HQ.lng], { icon: hqIcon, zIndexOffset: 1000 }).addTo(map)
      .bindPopup(`<strong>Legend Team S.r.l.</strong><br/>Strada Perticara 13<br/>47866 Sant'Agata Feltria (RN)<br/><a href="https://www.google.com/maps/search/?api=1&query=Legend+Team+srl+Sant'Agata+Feltria" target="_blank" rel="noreferrer">Apri su Google Maps</a>`);
    mapObj.current = map;
  }, [races]); // eslint-disable-line

  // pan to selected (only after the user actively picks an event — keep the Italy/HQ overview on open)
  useEffect(() => {
    if (mapObj.current && selected && selected.lat != null && userPan.current) {
      mapObj.current.setView([selected.lat, selected.lng], 8, { animate: true });
      markers.current[selected.id]?.openPopup();
    }
    // On mobile the detail opens inline under the tapped row — keep that row in view
    if (userPan.current && selected && window.innerWidth < 1024) {
      requestAnimationFrame(() => document.getElementById(`race-row-${selected.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }));
    }
  }, [selected]);

  const matches = (r) =>
    (discipline === "Tutti" || (r.discipline || "Rally") === discipline) &&
    (region === "Tutti" || (r.region || "Italia") === region) &&
    (year === "Tutti" || String(new Date(r.date).getFullYear()) === year);

  const upcoming = races.filter((r) => isUpcoming(r.date) && matches(r));
  const past = races.filter((r) => !isUpcoming(r.date) && matches(r));

  const availableDisc = [...new Set(races.map((r) => r.discipline || "Rally"))];
  const discOrder = [...DISC_ORDER.filter((d) => availableDisc.includes(d)), ...availableDisc.filter((d) => !DISC_ORDER.includes(d))];
  const years = [...new Set(races.map((r) => String(new Date(r.date).getFullYear())))].sort();

  const base = (r) => (region === "Tutti" || (r.region || "Italia") === region) && (year === "Tutti" || String(new Date(r.date).getFullYear()) === year);
  const countDisc = (d) => races.filter((r) => (d === "Tutti" || (r.discipline || "Rally") === d) && base(r)).length;
  const countRegion = (rg) => races.filter((r) => (discipline === "Tutti" || (r.discipline || "Rally") === discipline) && (rg === "Tutti" || (r.region || "Italia") === rg) && (year === "Tutti" || String(new Date(r.date).getFullYear()) === year)).length;
  const countYear = (y) => races.filter((r) => (discipline === "Tutti" || (r.discipline || "Rally") === discipline) && (region === "Tutti" || (r.region || "Italia") === region) && (y === "Tutti" || String(new Date(r.date).getFullYear()) === y)).length;

  const RaceRow = ({ r }) => (
    <>
    <button
      onClick={() => { userPan.current = true; setSelected({ ...r }); }}
      data-testid={`calendar-race-${r.id}`}
      id={`race-row-${r.id}`}
      className={`scroll-mt-24 w-full text-left p-4 rounded-sm border transition-all flex items-start gap-3 ${selected?.id === r.id ? "border-legend-red bg-legend-red/5" : "border-slate-200 dark:border-slate-800 hover:border-legend-red bg-white dark:bg-legend-paper"}`}
    >
      <div className="text-center shrink-0 w-14">
        <p className="font-display font-black text-2xl leading-none">{new Date(r.date).getDate()}</p>
        <p className="text-[10px] font-mono uppercase text-slate-500">{new Date(r.date).toLocaleDateString(t("dateLocale"), { month: "short" })}</p>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm ${catColor[r.category] || catColor.Rally}`}>{dc(r)}</span>
          {isUpcoming(r.date) && <span className="text-[10px] font-mono text-legend-red font-bold uppercase">{lang === "en" ? "Upcoming" : (r.status || "In programma")}</span>}
        </div>
        <h3 className="font-display font-bold uppercase leading-tight mt-1 truncate">{dn(r)}</h3>
        <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin size={11} /> {r.location} · {r.date_label || fmt(r.date)}</p>
      </div>
      <ChevronRight size={16} className={`text-slate-400 mt-1 transition-transform ${isMobile && selected?.id === r.id ? "rotate-90" : ""}`} />
    </button>
    {isMobile && selected?.id === r.id && <div className="animate-fade-up">{detailCard}</div>}
    </>
  );

  const detailCard = selected && (
            <div className="rounded-sm border border-slate-200 dark:border-slate-800 bg-white dark:bg-legend-paper p-5" data-testid="calendar-detail">
              <div className="flex items-start justify-between gap-3">
                <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm ${catColor[selected.category] || catColor.Rally}`}>{dc(selected)}</span>
                {selected.logo && <img src={selected.logo} alt="" data-testid="calendar-event-logo" className="h-10 object-contain" />}
              </div>
              <h3 className="font-display font-black uppercase text-2xl leading-tight mt-2">{dn(selected)}</h3>
              {selected.championship && <p className="text-xs text-slate-500">{selected.championship}</p>}
              {selected.organizer && (
                <p className="text-xs text-slate-500 mt-1">Organizzato da{" "}
                  {selected.organizer_url
                    ? <a href={selected.organizer_url} target="_blank" rel="noreferrer" className="text-legend-red underline font-semibold">{selected.organizer}</a>
                    : <span className="font-semibold">{selected.organizer}</span>}
                </p>
              )}
              <p className="text-sm mt-2 flex items-center gap-2"><MapPin size={14} className="text-legend-red" /> {selected.location}</p>
              <p className="text-sm flex items-center gap-2"><Calendar size={14} className="text-legend-red" /> {selected.date_label || fmt(selected.date)}</p>
              {selected.poster && (
                <a href={selected.official_url || selected.poster} target="_blank" rel="noreferrer" data-testid="calendar-poster" className="block mt-3 group">
                  <img src={selected.poster} alt={dn(selected)} className="w-full max-h-96 object-contain rounded-sm border border-slate-200 dark:border-slate-800 bg-slate-50" />
                  <span className="mt-1 block text-[10px] font-mono uppercase tracking-widest text-slate-400 group-hover:text-legend-red">{t("sourceLabel")}</span>
                </a>
              )}
              {selected.orari?.length > 0 && (
                <div className="mt-3">
                  <p className="font-display font-bold uppercase text-sm flex items-center gap-1 mb-1"><Clock size={14} className="text-legend-red" /> {t("program")}</p>
                  <ul className="space-y-1">
                    {selected.orari.map((o, i) => <li key={i} className="text-xs text-slate-600 dark:text-slate-300 flex gap-2"><span className="text-legend-red">▸</span>{o}</li>)}
                  </ul>
                </div>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                {selected.live_url && (
                  <a href={selected.live_url} target="_blank" rel="noreferrer" data-testid="calendar-live" className="inline-flex items-center gap-1.5 bg-legend-red text-white text-sm font-bold uppercase px-4 py-2 rounded-sm hover:bg-legend-dark">
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> {t("watchLive")}
                  </a>
                )}
                {selected.official_url && (
                  <a href={selected.official_url} target="_blank" rel="noreferrer" data-testid="calendar-official" className="inline-flex items-center gap-1.5 bg-legend-ink text-white text-sm font-bold uppercase px-4 py-2 rounded-sm hover:bg-slate-800">
                    <Globe size={14} /> {t("officialSite")}
                  </a>
                )}
                {selected.komoot_url && (
                  <a href={selected.komoot_url} target="_blank" rel="noreferrer" data-testid="calendar-komoot" className="inline-flex items-center gap-1.5 bg-[#6AA127] text-white text-sm font-bold uppercase px-4 py-2 rounded-sm hover:brightness-95">
                    <Download size={14} /> {lang === "en" ? "Komoot track" : "Traccia Komoot"}
                  </a>
                )}
                {selected.tickets_url && (
                  <a href={selected.tickets_url} target="_blank" rel="noreferrer" data-testid="calendar-tickets" className="inline-flex items-center gap-1.5 bg-legend-yellow text-legend-ink text-sm font-bold uppercase px-4 py-2 rounded-sm hover:brightness-95">
                    <Ticket size={14} /> {t("ticketsLabel")}
                  </a>
                )}
                {selected.article_slug && (
                  <button onClick={() => navigate(`/articolo/${selected.article_slug}`)} data-testid="calendar-read-article" className="inline-flex items-center gap-1.5 bg-legend-ink text-white text-sm font-bold uppercase px-4 py-2 rounded-sm hover:bg-slate-800">
                    {t("readArticle")}
                  </button>
                )}
                {selected.registration_deadline && (
                  <a href={LEGENDTEAM_URL} target="_blank" rel="noreferrer" data-testid="calendar-register" className="inline-flex items-center gap-1.5 bg-legend-red text-white text-sm font-bold uppercase px-4 py-2 rounded-sm hover:bg-legend-dark">
                    <Flag size={14} /> {t("registerBy")} {fmt(selected.registration_deadline)})
                  </a>
                )}
              </div>
            </div>
  );

  const chip = (active) => `inline-flex items-center gap-1.5 px-3.5 py-2 rounded-sm text-sm font-bold uppercase tracking-wide transition-colors ${active ? "bg-legend-red text-white" : "bg-white dark:bg-legend-paper border border-slate-200 dark:border-slate-800 hover:border-legend-red"}`;
  const cnt = (active, n) => <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-sm ${active ? "bg-white/25" : "bg-slate-100 dark:bg-white/10 text-slate-500"}`}>{n}</span>;
  const flabel = (txt) => <p className="text-[11px] font-mono uppercase tracking-widest text-slate-400 mb-2 flex items-center gap-1.5"><Filter size={12} /> {txt}</p>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-10" data-testid="calendario-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
        <div className="flex items-center gap-3">
          <Calendar size={26} className="text-legend-red" />
          <h1 className="font-display font-black uppercase text-4xl md:text-5xl leading-none">{t("calTitle")}</h1>
        </div>
        <ProposeEventDialog lang={lang} />
      </div>
      <p className="text-slate-500 mb-6">{t("calSub")}</p>

      {/* Filters */}
      <div className="mb-8 rounded-sm border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-white/[0.03] p-4 space-y-4" data-testid="calendar-filters">
        <div>
          {flabel(lang === "en" ? "Discipline" : "Disciplina")}
          <div className="flex flex-wrap gap-2" data-testid="discipline-filter">
            {discOrder.map((d) => (
              <button key={d} onClick={() => setDiscipline(d)} data-testid={`discipline-${d.toLowerCase()}`} className={chip(discipline === d)}>{d} {cnt(discipline === d, countDisc(d))}</button>
            ))}
            <button onClick={() => setDiscipline("Tutti")} data-testid="discipline-tutti" className={chip(discipline === "Tutti")}>{t("allEvents")} {cnt(discipline === "Tutti", countDisc("Tutti"))}</button>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            {flabel(lang === "en" ? "Area" : "Zona")}
            <div className="flex flex-wrap gap-2" data-testid="region-filter">
              {["Tutti", "Italia", "Europa", "Mondo"].map((r) => (
                <button key={r} onClick={() => setRegion(r)} data-testid={`region-${r.toLowerCase()}`} className={chip(region === r)}>{r === "Tutti" ? t("allEvents") : r} {cnt(region === r, countRegion(r))}</button>
              ))}
            </div>
          </div>
          <div>
            {flabel(lang === "en" ? "Year" : "Anno")}
            <div className="flex flex-wrap gap-2" data-testid="year-filter">
              {years.map((y) => (
                <button key={y} onClick={() => setYear(y)} data-testid={`year-${y}`} className={chip(year === y)}>{y} {cnt(year === y, countYear(y))}</button>
              ))}
              <button onClick={() => setYear("Tutti")} data-testid="year-tutti" className={chip(year === "Tutti")}>{t("allEvents")} {cnt(year === "Tutti", countYear("Tutti"))}</button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-8">
        {/* List */}
        <div className="space-y-6 min-w-0">
          <div>
            <h2 className="font-display font-bold uppercase text-lg text-legend-red mb-3 flex items-center gap-2"><Flag size={16} /> {t("upcomingRaces")}</h2>
            <div className="space-y-2">
              {upcoming.map((r) => <RaceRow key={r.id} r={r} />)}
              {upcoming.length === 0 && <p className="text-slate-500 text-sm">{t("noUpcoming")}</p>}
            </div>
          </div>
          <div>
            <h2 className="font-display font-bold uppercase text-lg text-slate-500 mb-3 flex items-center gap-2"><Trophy size={16} /> {t("pastRaces")}</h2>
            <div className="space-y-2">
              {past.map((r) => <RaceRow key={r.id} r={r} />)}
            </div>
          </div>
        </div>

        {/* Map + detail */}
        <div ref={detailRef} className="lg:sticky lg:top-20 h-fit space-y-4 scroll-mt-4 min-w-0">
          <div ref={mapRef} data-testid="calendar-map" className="w-full h-[380px] rounded-sm overflow-hidden border border-slate-200 dark:border-slate-800 z-0" />
          {!isMobile && detailCard}
        </div>
      </div>
    </div>
  );
}
